import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  POPULAR_ROUTES_BY_COUNTRY,
  getCityByKey,
  buildSearchUrl,
} from "../data/featuredNetwork";
import { SectionTitle } from "./layout";

/**
 * Country columns with one-click OD search links.
 * Mobile: accordion per country; desktop: equal CSS grid columns.
 */
export default function PopularRouteLists() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expanded, setExpanded] = useState(
    POPULAR_ROUTES_BY_COUNTRY[0]?.countryKey ?? false
  );

  const openQuickRoute = (fromKey, toKey) => {
    const from = getCityByKey(fromKey);
    const to = getCityByKey(toKey);
    if (!from || !to) return;
    navigate(buildSearchUrl({ fromName: from.name, toName: to.name }));
  };

  const renderRoutes = (group) => (
    <Stack spacing={1}>
      {group.routes.map(({ fromKey, toKey }) => (
        <Stack
          key={`${fromKey}-${toKey}`}
          direction="row"
          spacing={1}
          alignItems="flex-start"
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 18,
              color: "primary.main",
              flexShrink: 0,
              mt: "2px",
            }}
          />
          <Link
            component="button"
            type="button"
            underline="always"
            onClick={() => openQuickRoute(fromKey, toKey)}
            sx={{
              textAlign: "left",
              color: "text.secondary",
              fontSize: { xs: "0.9rem", lg: "0.85rem" },
              fontWeight: 500,
              cursor: "pointer",
              border: 0,
              background: "none",
              p: 0,
              lineHeight: 1.35,
              "&:hover": { color: "primary.main" },
            }}
          >
            {t(`popularTrips.cities.${fromKey}`)} -{" "}
            {t(`popularTrips.cities.${toKey}`)}
          </Link>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Box sx={{ width: "100%", py: { xs: 3, sm: 4 } }}>
      <SectionTitle
        title={t("popularTrips.quickRoutes")}
        variant="h5"
        sx={{
          mb: 3,
          "& .MuiTypography-h5": {
            fontWeight: 800,
          },
        }}
      />

      {isMobile ? (
        <Box>
          {POPULAR_ROUTES_BY_COUNTRY.map((group) => (
            <Accordion
              key={group.countryKey}
              disableGutters
              elevation={0}
              expanded={expanded === group.countryKey}
              onChange={(_, isExpanded) =>
                setExpanded(isExpanded ? group.countryKey : false)
              }
              sx={{
                bgcolor: "transparent",
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
                sx={{ px: 0 }}
              >
                <Typography variant="subtitle1" fontWeight={800} color="text.primary">
                  {t(`popularTrips.countries.${group.countryKey}`)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
                {renderRoutes(group)}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            width: "fit-content",
            maxWidth: "100%",
            mx: "auto",
            columnGap: { sm: 6, md: 10 },
            gridTemplateColumns: "repeat(2, max-content)",
            alignItems: "start",
          }}
        >
          {POPULAR_ROUTES_BY_COUNTRY.map((group) => (
            <Box key={group.countryKey} sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                color="text.primary"
                mb={1.5}
              >
                {t(`popularTrips.countries.${group.countryKey}`)}:
              </Typography>
              {renderRoutes(group)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
